import { useState, useEffect, useRef } from "react";
import CardConfrontation from "./cardConfrontation/cardConfrontation";
import { IMG_BASE } from "../simulatorLiguilla";
import "./carrouselConfrontation.sass";

export default function CarrouselConfrontation({ data }) {

  const classPrefix = "carrousel-enfrentamientos";
  const classCarrousel = "enfrentamientos-card";

  const [cardWidth, setCardWidth] = useState(150);
  
  // Detectar tipo de datos: ¿Es standings (con finalStandings) o matches (array)?
  const isStandings = data?.finalStandings;
  const isMatches = Array.isArray(data) && data[0]?.HomeTeamId;
  
  let confrontations = [];

  if (isStandings) {
    // Modo: Cuartos de final desde standings (top 8)
    const allStandings = data.finalStandings || [];
    const top8Teams = allStandings.slice(0, 8);
    const totalTop8 = top8Teams.length;
    
    confrontations = Array.from({ length: Math.floor(totalTop8 / 2) }, (_, i) => {
      const home = top8Teams[i];
      const away = top8Teams[totalTop8 - 1 - i];
      
      return {
        id: i,
        home: { ...home, img: IMG_BASE + home?.contestantId, rank: home?.rank },
        away: { ...away, img: IMG_BASE + away?.contestantId, rank: away?.rank },
      };
    });
  } else if (isMatches) {
    // Modo: Partidos reales desde events    
    confrontations = data.map((match, i) => {
      return {
        id: i,
        home: { 
          contestantShortName: match.homeTeam,
          contestantId: match.HomeTeamId,
          image: match.homeTeamImg,
          img: match.homeTeamImg,
          rank: i + 1
        },
        away: { 
          contestantShortName: match.awayTeam,
          contestantId: match.AwayTeamId,
          image: match.awayTeamImg,
          img: match.awayTeamImg,
          rank: i + 1
        },
        homeGoals: match.homeGoals,
        awayGoals: match.awayGoals,
        eventPhase: match.eventPhase,
        venueName: match.venueName
      };
    });
  }

  const sliderRef = useRef(null);
  // Estados para scroll y control de UI
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isEndReached, setIsEndReached] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  // Estados para control táctil y dimensiones
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [newWidth, setNewWidth] = useState(0);

  /** Detecta si el contenedor es scrollable al renderizar o redimensionar */
  useEffect(() => {
    const container = sliderRef.current;
    if (container) {
      setIsScrollable(container.scrollWidth > container.clientWidth);
      const firstItem = container.querySelector(`.${classCarrousel}`);
      if (firstItem) setCardWidth(firstItem.getBoundingClientRect().width);
    }
  }, [data, newWidth]);

  /** Detecta redimensionamiento de ventana */
  useEffect(() => {
    const onResize = () => setNewWidth(window.innerWidth);

    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); };
  }, []);

  // Swipe táctil (izquierda / derecha)
  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const handleSwipe = () => {
    if (((touchStart - touchEnd) > 100) && currentIndex < confrontations.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    if (((touchEnd - touchStart) > 100) && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Manejo del scroll con botones left / right
  useEffect(() => {
    const container = sliderRef.current;
    if (!container) return;

    const onScroll = () => {
      setScrollLeft(container.scrollLeft);
      setIsEndReached(container.scrollLeft + container.clientWidth >= container.scrollWidth - 1);
    };

    container.addEventListener('scroll', onScroll);
    return () => {
      container.removeEventListener('scroll', onScroll);
      setNewWidth(container?.clientWidth);
    };
  }, [sliderRef.current]);

  // Función para mover scroll del carrusel a traves de las flechas
  const moveScroll = (direction) => {
    const container = sliderRef.current;
    if (!container) return;

    const firstItem = container.querySelector(`.${classCarrousel}`);
    if (!firstItem) return;

    const itemWidth = firstItem.getBoundingClientRect().width;
    const visibleItems = Math.max(1, Math.floor(container.clientWidth / itemWidth));
    const scrollLeftNew = container.scrollLeft;

    const newScroll = direction === 'next'
      ? (Math.ceil(scrollLeftNew / itemWidth) + visibleItems) * itemWidth
      : (Math.floor(scrollLeftNew / itemWidth) - visibleItems) * itemWidth;

    container.scrollTo({ left: newScroll, behavior: 'smooth' });
  };

  return (
    <div className="container__carrousel-confrontation">
      <div className={`${classPrefix}__arrows`}>
        {/* Flecha izquierda si hay scroll */}
        {scrollLeft >= 1 && confrontations.length > 0 && (
          <button type="button" className={`${classPrefix}__arrows-arrowL`} onClick={() => moveScroll("prev")} aria-label="Atrás" />
        )}

        {/* Contenedor carrousel deslizable */}
        <div className={`${classPrefix}__slider`} ref={sliderRef}>
          <div
            className={`${classPrefix}__slider-slides`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleSwipe}
          >
            {confrontations.map((item, index) => (
              <div className={`${classCarrousel}`} key={item.id + "-" + index}>
                <CardConfrontation item={item} />
              </div>
            ))}

          </div>
        </div>

        {/* Flecha derecha si hay scroll disponible */}
        {(isScrollable && !isEndReached && (((cardWidth * confrontations?.length) >= newWidth))) && confrontations.length > 0 && (
          <button type="button" className={`${classPrefix}__arrows-arrowR`} onClick={() => moveScroll("next")} aria-label="Adelante" />
        )}
      </div>
    </div>
  )
}